import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { ConfirmDialog } from "../components/ui";
import RowActionsMenu, { IconTrash, IconEye, IconEyeOff } from "../components/RowActionsMenu";

interface Announcement {
  id: string;
  title: string;
  body: string;
  type: string;
  is_manual: boolean;
  is_active: boolean;
  target_org_id: string | null;
  created_at: string;
  expires_at: string | null;
}

interface Organization {
  id: string;
  name: string;
  plan: string;
  auto_notifications: { portfolio_changes: boolean } | null;
}

// v3 liquid glass: el color del tipo SOLO vive en el punto semántico (y en la
// simulación de la notificación real dentro del preview, que no es chrome de esta página).
const TYPE_DOT: Record<string, string> = {
  info:    "#0891b2",
  warning: "#f59e0b",
  success: "#10b981",
  feature: "#8b5cf6",
};

const TYPE_META: Record<string, { label: string; icon: React.ReactElement }> = {
  info: {
    label: "Información",
    icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>,
  },
  warning: {
    label: "Aviso",
    icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><path d="M12 9v4M12 17h.01"/></svg>,
  },
  success: {
    label: "Confirmación",
    icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><path d="M22 4L12 14.01l-3-3"/></svg>,
  },
  feature: {
    label: "Novedad",
    icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3c-1.2 5.4-5.4 7.2-9 9 3.6 1.8 7.8 3.6 9 9 1.2-5.4 5.4-7.2 9-9-3.6-1.8-7.8-3.6-9-9z"/></svg>,
  },
};

const PLAN_STYLE: Record<string, { bg: string; color: string }> = {
  core:  { bg: "#3b82f620", color: "#3b82f6" },
  pro:   { bg: "#8b5cf620", color: "#8b5cf6" },
  ultra: { bg: "#f59e0b20", color: "#f59e0b" },
};

const card: React.CSSProperties = {
  background: "var(--clr-surface)",
  border: "1px solid var(--clr-border)",
  borderRadius: 18,
  backdropFilter: "var(--clr-blur)",
  WebkitBackdropFilter: "var(--clr-blur)",
  boxShadow: "var(--clr-shadow-sm)",
  padding: "1.25rem 1.5rem",
};

const th: React.CSSProperties = {
  padding: "0.5rem 0.75rem",
  fontSize: "0.65rem",
  fontWeight: 600,
  letterSpacing: 0.9,
  textTransform: "uppercase",
  color: "var(--clr-text4)",
  textAlign: "left",
  borderBottom: "1px solid var(--clr-border2)",
};

const td: React.CSSProperties = {
  padding: "0.65rem 0.75rem",
  fontSize: "0.85rem",
  color: "var(--clr-text)",
  borderBottom: "1px solid var(--clr-border2)",
  verticalAlign: "middle",
};

// Tipo = cápsula tintada suave SIN punto (el punto es de estados)
function TypeBadge({ type }: { type: string }) {
  const dot = TYPE_DOT[type] ?? "#6b7280";
  const label = TYPE_META[type]?.label ?? type;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center",
      background: dot + "1e", color: dot,
      borderRadius: 999, padding: "3px 11px",
      fontSize: "0.72rem", fontWeight: 600, whiteSpace: "nowrap",
    }}>
      {label}
    </span>
  );
}

function PlanBadge({ plan }: { plan: string }) {
  const s = PLAN_STYLE[plan] ?? { bg: "#6b728020", color: "#6b7280" };
  return (
    <span style={{ background: s.bg, color: s.color, padding: "2px 8px", borderRadius: 20, fontSize: "0.7rem", fontWeight: 600 }}>
      {plan}
    </span>
  );
}

function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      onClick={() => onChange(!checked)}
      style={{
        width: 42, height: 24, borderRadius: 12,
        background: checked ? "var(--clr-org-primary)" : "var(--clr-border)",
        border: "none", cursor: "pointer", position: "relative",
        transition: "background 0.2s", flexShrink: 0,
      }}
    >
      <span style={{
        position: "absolute", top: 3,
        left: checked ? 20 : 3,
        width: 18, height: 18, borderRadius: "50%",
        background: "#fff",
        transition: "left 0.2s",
        display: "block",
      }} />
    </button>
  );
}

// Preview modal — shows exactly how the notification looks to users
function PreviewModal({ ann, orgName, onClose }: { ann: Announcement; orgName: string; onClose: () => void }) {
  const dotColor = TYPE_DOT[ann.type] ?? "#6b7280";
  const meta = TYPE_META[ann.type] ?? TYPE_META.info;

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 1100,
        background: "rgba(11,18,21,0.4)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "1rem",
      }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div style={{
        background: "var(--clr-modal-solid)",
        border: "1px solid var(--clr-border)",
        borderRadius: 22,
        width: "100%", maxWidth: 500,
        boxShadow: "0 24px 64px rgba(0,0,0,0.25)",
        overflow: "hidden",
      }}>

        {/* Header */}
        <div style={{
          padding: "1rem 1.25rem",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          borderBottom: "1px solid var(--clr-border2)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ width: 7, height: 7, borderRadius: 4, background: dotColor, display: "inline-block", flexShrink: 0 }} />
            <span style={{ fontSize: "0.9rem", fontWeight: 700, letterSpacing: "-0.01em", color: "var(--clr-text)" }}>
              {ann.title}
            </span>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "var(--clr-modal-solid2)", border: "1px solid var(--clr-border2)", cursor: "pointer",
              color: "var(--clr-text3)", width: 30, height: 30, borderRadius: 15,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: "0.8rem", lineHeight: 1, flexShrink: 0,
            }}
          >✕</button>
        </div>

        {/* Body */}
        <div style={{ padding: "1.25rem 1.5rem" }}>
          {/* How users see it */}
          <p style={{ fontSize: "0.72rem", fontWeight: 600, color: "var(--clr-text4)", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: "0.75rem" }}>
            Así lo verán los usuarios
          </p>

          {/* Notification card simulation (réplica de cómo lo ve el usuario — no chrome de esta página) */}
          <div style={{
            background: "rgba(8,145,178,0.05)",
            border: `1px solid ${dotColor}30`,
            borderLeft: `3px solid ${dotColor}`,
            borderRadius: 10,
            padding: "0.9rem 1rem",
            display: "flex", gap: "0.75rem", alignItems: "flex-start",
          }}>
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#0891b2", flexShrink: 0, marginTop: 5 }} />
            <span style={{ color: dotColor, flexShrink: 0, display: "flex", alignItems: "center" }}>{meta.icon}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: "0.88rem", color: "var(--clr-text)", marginBottom: 5 }}>
                {ann.title}
              </div>
              <div style={{ fontSize: "0.83rem", color: "var(--clr-text2)", lineHeight: 1.55, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {ann.body}
              </div>
            </div>
          </div>

          {/* Meta info row */}
          <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", marginTop: "1rem", alignItems: "center" }}>
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              background: dotColor + "1e", color: dotColor,
              borderRadius: 999, padding: "3px 11px",
              fontSize: "0.72rem", fontWeight: 600, whiteSpace: "nowrap",
            }}>
              <span style={{ width: 6, height: 6, borderRadius: 3, background: dotColor, display: "inline-block", flexShrink: 0 }} />
              {meta.label}
            </span>
            <span style={{ fontSize: "0.75rem", color: "var(--clr-text3)" }}>
              {orgName}
            </span>
            {ann.expires_at && (
              <span style={{ fontSize: "0.75rem", color: "var(--clr-text4)" }}>
                Expira {new Date(ann.expires_at).toLocaleDateString("es-ES")}
              </span>
            )}
            {(() => {
              const c = ann.is_active ? "#10b981" : "#94a3b8";
              return (
                <span style={{
                  marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 6,
                  background: c + "1e", color: c,
                  borderRadius: 999, padding: "3px 11px",
                  fontSize: "0.72rem", fontWeight: 600, whiteSpace: "nowrap",
                }}>
                  {ann.is_active ? "Activo" : "Inactivo"}
                </span>
              );
            })()}
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: "0.9rem 1.5rem",
          borderTop: "1px solid var(--clr-border2)",
          display: "flex", justifyContent: "flex-end",
        }}>
          <button
            onClick={onClose}
            style={{
              padding: "0.5rem 1.3rem",
              border: "1px solid var(--clr-border2)",
              borderRadius: 999,
              background: "var(--clr-modal-solid2)",
              color: "var(--clr-text2)",
              cursor: "pointer",
              fontSize: "0.8rem",
              fontWeight: 600,
            }}
          >
            Cerrar
          </button>
        </div>

      </div>
    </div>
  );
}

// Modal for creating/editing an announcement
function AnnModal({
  orgs,
  onClose,
  onSave,
}: {
  orgs: Organization[];
  onClose: () => void;
  onSave: (ann: Announcement) => void;
}) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [type, setType] = useState("info");
  const [targetOrg, setTargetOrg] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim() || !body.trim()) { setErr("Título y cuerpo son obligatorios."); return; }
    setSaving(true);
    setErr(null);
    try {
      const ann = await api.post<Announcement>("/superadmin/announcements", {
        title: title.trim(),
        body: body.trim(),
        type,
        target_org_id: targetOrg || null,
        expires_at: expiresAt || null,
      });
      onSave(ann);
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "Error al crear el aviso");
      setSaving(false);
    }
  }

  const inputStyle: React.CSSProperties = {
    width: "100%", padding: "0.55rem 0.75rem",
    background: "var(--clr-input-bg)",
    border: "1px solid var(--clr-border)", borderRadius: 12,
    color: "var(--clr-text)", fontSize: "0.875rem", outline: "none",
    boxSizing: "border-box",
  };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 1000,
      background: "rgba(11,18,21,0.4)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: "1rem",
    }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <form
        onSubmit={submit}
        style={{
          background: "var(--clr-modal-solid)", border: "1px solid var(--clr-border)",
          borderRadius: 22, padding: "1.75rem", width: "100%", maxWidth: 520,
          boxShadow: "0 24px 64px rgba(0,0,0,0.25)",
        }}
      >
        <h2 style={{ fontSize: "1.1rem", fontWeight: 700, letterSpacing: "-0.02em", color: "var(--clr-text)", marginBottom: "1.25rem" }}>
          Nuevo aviso
        </h2>

        <div style={{ display: "flex", flexDirection: "column", gap: "0.9rem" }}>
          <div>
            <label style={{ fontSize: "0.78rem", fontWeight: 600, color: "var(--clr-text3)", display: "block", marginBottom: 4 }}>Título *</label>
            <input type="text" value={title} onChange={e => setTitle(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={{ fontSize: "0.78rem", fontWeight: 600, color: "var(--clr-text3)", display: "block", marginBottom: 4 }}>Cuerpo *</label>
            <textarea
              value={body} onChange={e => setBody(e.target.value)}
              rows={4}
              style={{ ...inputStyle, resize: "vertical", fontFamily: "inherit" }}
            />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
            <div>
              <label style={{ fontSize: "0.78rem", fontWeight: 600, color: "var(--clr-text3)", display: "block", marginBottom: 4 }}>Tipo</label>
              <select value={type} onChange={e => setType(e.target.value)} style={{ ...inputStyle, cursor: "pointer" }}>
                <option value="info">Info</option>
                <option value="warning">Warning</option>
                <option value="success">Success</option>
                <option value="feature">Feature</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: "0.78rem", fontWeight: 600, color: "var(--clr-text3)", display: "block", marginBottom: 4 }}>Destino</label>
              <select value={targetOrg} onChange={e => setTargetOrg(e.target.value)} style={{ ...inputStyle, cursor: "pointer" }}>
                <option value="">Todos los usuarios</option>
                {orgs.map(o => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label style={{ fontSize: "0.78rem", fontWeight: 600, color: "var(--clr-text3)", display: "block", marginBottom: 4 }}>Expira (opcional)</label>
            <input type="datetime-local" value={expiresAt} onChange={e => setExpiresAt(e.target.value)} style={inputStyle} />
          </div>
        </div>

        {err && (
          <div style={{ marginTop: "0.75rem", color: "#ef4444", fontSize: "0.83rem" }}>
            {err}
          </div>
        )}

        <div style={{ display: "flex", gap: "0.75rem", marginTop: "1.25rem", justifyContent: "flex-end" }}>
          <button type="button" onClick={onClose} style={{ padding: "0.55rem 1.1rem", border: "1px solid var(--clr-border)", borderRadius: 999, background: "transparent", color: "var(--clr-text3)", cursor: "pointer", fontSize: "0.85rem" }}>
            Cancelar
          </button>
          <button type="submit" disabled={saving} style={{ padding: "0.55rem 1.25rem", border: "none", borderRadius: 999, background: "var(--clr-org-primary)", color: "#fff", cursor: saving ? "not-allowed" : "pointer", fontWeight: 600, fontSize: "0.85rem", opacity: saving ? 0.7 : 1 }}>
            {saving ? "Guardando…" : "Publicar aviso"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function SuperAdminAvisosPage() {
  const [tab, setTab] = useState<"published" | "auto">("published");
  const [anns, setAnns] = useState<Announcement[]>([]);
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [previewAnn, setPreviewAnn] = useState<Announcement | null>(null);
  const [deleteAnnId, setDeleteAnnId] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    try {
      const [annData, orgData] = await Promise.all([
        api.get<{ items: Announcement[] }>("/superadmin/announcements"),
        api.get<Organization[]>("/superadmin/notification-settings"),
      ]);
      setAnns(annData.items);
      setOrgs(orgData);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function toggleActive(ann: Announcement) {
    try {
      const updated = await api.put<Announcement>(`/superadmin/announcements/${ann.id}`, {
        is_active: !ann.is_active,
      });
      setAnns(prev => prev.map(a => a.id === ann.id ? updated : a));
    } catch {
      // ignore
    }
  }

  async function deleteAnn(id: string) {
    setDeleteAnnId(null);
    try {
      await api.delete(`/superadmin/announcements/${id}`);
      setAnns(prev => prev.filter(a => a.id !== id));
    } catch {
      // ignore
    }
  }

  async function toggleOrgNotification(orgId: string, value: boolean) {
    try {
      await api.put(`/superadmin/notification-settings/${orgId}`, { portfolio_changes: value });
      setOrgs(prev => prev.map(o => o.id === orgId
        ? { ...o, auto_notifications: { ...(o.auto_notifications ?? {}), portfolio_changes: value } }
        : o
      ));
    } catch {
      // ignore
    }
  }

  const tabStyle = (active: boolean): React.CSSProperties => ({
    padding: "0.42rem 1rem",
    border: active ? "1px solid transparent" : "1px solid var(--clr-border)",
    borderRadius: 999,
    background: active ? "var(--clr-org-primary)" : "var(--clr-surface)",
    color: active ? "#fff" : "var(--clr-text3)",
    cursor: "pointer",
    fontSize: "0.78rem",
    fontWeight: active ? 600 : 500,
    transition: "all 0.15s",
  });

  return (
    <div style={{ padding: "2rem", fontVariantNumeric: "tabular-nums" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1.75rem", flexWrap: "wrap", gap: "1rem" }}>
        <h1 style={{ fontSize: "1.4rem", fontWeight: 700, letterSpacing: "-0.02em", color: "var(--clr-text)", margin: 0 }}>
          Avisos y Notificaciones
        </h1>
        <button
          onClick={() => setShowModal(true)}
          style={{ padding: "0.6rem 1.25rem", background: "var(--clr-org-primary)", color: "#fff", border: "none", borderRadius: 999, cursor: "pointer", fontWeight: 600, fontSize: "0.875rem" }}
        >
          + Nuevo aviso
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1.25rem" }}>
        <button style={tabStyle(tab === "published")} onClick={() => setTab("published")}>
          Avisos publicados ({anns.length})
        </button>
        <button style={tabStyle(tab === "auto")} onClick={() => setTab("auto")}>
          Notificaciones automáticas
        </button>
      </div>

      {loading ? (
        <div style={{ color: "var(--clr-text4)", padding: "3rem", textAlign: "center" }}>Cargando…</div>
      ) : tab === "published" ? (
        // ── Published announcements ─────────────────────────────────────────
        <div style={card}>
          {anns.length === 0 ? (
            <div style={{ color: "var(--clr-text4)", textAlign: "center", padding: "2rem" }}>
              No hay avisos publicados.
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={th}>Título</th>
                  <th style={th}>Tipo</th>
                  <th style={th}>Destino</th>
                  <th style={th}>Creado</th>
                  <th style={th}>Expira</th>
                  <th style={th}>Estado</th>
                  <th style={th}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {anns.map(ann => {
                  const org = orgs.find(o => o.id === ann.target_org_id);
                  return (
                    <tr key={ann.id}>
                      <td
                        style={{ ...td, cursor: "pointer" }}
                        onClick={() => setPreviewAnn(ann)}
                        title="Ver previsualización"
                      >
                        <div style={{ fontWeight: 600, maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "var(--clr-text)", display: "flex", alignItems: "center", gap: 6 }}>
                          {ann.title}
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.35, flexShrink: 0 }}>
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                          </svg>
                        </div>
                        <div style={{ fontSize: "0.75rem", color: "var(--clr-text4)", marginTop: 2, maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{ann.body}</div>
                      </td>
                      <td style={td}><TypeBadge type={ann.type} /></td>
                      <td style={td}>
                        {org ? (
                          <span style={{ fontSize: "0.82rem", color: "var(--clr-text2)" }}>
                            {org.name}
                          </span>
                        ) : (
                          <span style={{ fontSize: "0.82rem", color: "var(--clr-text4)" }}>Todos</span>
                        )}
                      </td>
                      <td style={td}>
                        <span style={{ fontSize: "0.8rem", color: "var(--clr-text3)" }}>
                          {new Date(ann.created_at).toLocaleDateString("es-ES")}
                        </span>
                      </td>
                      <td style={td}>
                        <span style={{ fontSize: "0.8rem", color: ann.expires_at ? "var(--clr-text3)" : "var(--clr-text4)" }}>
                          {ann.expires_at ? new Date(ann.expires_at).toLocaleDateString("es-ES") : "—"}
                        </span>
                      </td>
                      <td style={td}>
                        {(() => {
                          const c = ann.is_active ? "#10b981" : "#94a3b8";
                          return (
                            <span style={{
                              display: "inline-flex", alignItems: "center", gap: 6,
                              background: c + "1e", color: c,
                              borderRadius: 999, padding: "3px 11px",
                              fontSize: "0.72rem", fontWeight: 600, whiteSpace: "nowrap",
                            }}>
                              {ann.is_active ? "Activo" : "Inactivo"}
                            </span>
                          );
                        })()}
                      </td>
                      <td style={td}>
                        <RowActionsMenu actions={[
                          {
                            label: ann.is_active ? "Desactivar" : "Activar",
                            icon: ann.is_active ? <IconEyeOff /> : <IconEye />,
                            onClick: () => toggleActive(ann),
                          },
                          {
                            label: "Eliminar",
                            icon: <IconTrash />,
                            onClick: () => setDeleteAnnId(ann.id),
                            danger: true,
                          },
                        ]} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      ) : (
        // ── Auto notifications ──────────────────────────────────────────────
        <div style={card}>
          <p style={{ fontSize: "0.83rem", color: "var(--clr-text4)", marginBottom: "1rem" }}>
            Configura qué notificaciones automáticas recibe cada organización.
          </p>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={th}>Organización</th>
                <th style={th}>Plan</th>
                <th style={th}>Cambios en portfolio</th>
              </tr>
            </thead>
            <tbody>
              {orgs.map(org => {
                const portfolioOn = org.auto_notifications?.portfolio_changes ?? true;
                return (
                  <tr key={org.id}>
                    <td style={td}>{org.name}</td>
                    <td style={td}><PlanBadge plan={org.plan} /></td>
                    <td style={td}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <Toggle
                          checked={portfolioOn}
                          onChange={v => toggleOrgNotification(org.id, v)}
                        />
                        <span style={{ fontSize: "0.8rem", color: "var(--clr-text3)" }}>
                          {portfolioOn ? "Activado" : "Desactivado"}
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <AnnModal
          orgs={orgs}
          onClose={() => setShowModal(false)}
          onSave={ann => {
            setAnns(prev => [ann, ...prev]);
            setShowModal(false);
          }}
        />
      )}

      {previewAnn && (
        <PreviewModal
          ann={previewAnn}
          orgName={orgs.find(o => o.id === previewAnn.target_org_id)?.name ?? "Todos los usuarios"}
          onClose={() => setPreviewAnn(null)}
        />
      )}

      <ConfirmDialog
        open={deleteAnnId !== null}
        title="¿Eliminar este aviso?"
        message="El aviso dejará de mostrarse a los usuarios. Esta acción no se puede deshacer."
        onConfirm={() => deleteAnnId && deleteAnn(deleteAnnId)}
        onCancel={() => setDeleteAnnId(null)}
      />
    </div>
  );
}
